import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@ophelos/ui/components/alert-dialog';
import { Button } from '@ophelos/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@ophelos/ui/components/card';
import { CalendarClock } from 'lucide-react';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { useTranslation } from '@/hooks/useTranslation';
import { SCHEDULING_MIN_LEAD_DAYS, type PaymentMethod } from '@ophelos/shared/constants/payment-methods';
import { isWithinLeadDays } from '@/lib/payment-schedule';

interface ScheduledPaymentPanelProps {
  /** Amount to be charged (major units). */
  amount: number | null;
  currency?: string;
  /** Scheduled date in YYYY-MM-DD. */
  scheduledDate: string | null;
  /** Method the scheduled payment will charge — decides whether cancelling
   *  still leaves an in-flight charge (e.g. Bacs, whose payment intent is
   *  created days before the due date). */
  paymentMethod?: PaymentMethod | string | null;
  onCancel: () => void;
  isCancelling: boolean;
  error?: string | null;
}

/**
 * Shown on the payment page when the debt already has a future-dated payment.
 * Presents it as a payment in progress (not a plan) and offers a cancel action.
 */
export function ScheduledPaymentPanel({
  amount,
  currency = 'EUR',
  scheduledDate,
  paymentMethod,
  onCancel,
  isCancelling,
  error,
}: ScheduledPaymentPanelProps) {
  const { t, currentLanguage } = useTranslation();
  const leadDays = paymentMethod ? SCHEDULING_MIN_LEAD_DAYS[paymentMethod as PaymentMethod] : undefined;
  const chargeAlreadyInFlight = leadDays !== undefined && !!scheduledDate && isWithinLeadDays(scheduledDate, leadDays);
  const formattedDate = scheduledDate
    ? new Date(`${scheduledDate}T00:00:00`).toLocaleDateString(currentLanguage, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

  return (
    <div className="xs:max-w-lg w-full mx-auto flex flex-col gap-6">
      <Card variant="fullscreen" className="gap-5">
        <CardHeader className="items-center gap-3 text-center">
          <div className="highlight-accent flex items-center justify-center rounded-full p-3">
            <CalendarClock className="size-6" />
          </div>
          <CardTitle>{t('payment.scheduledPanel.title')}</CardTitle>
        </CardHeader>
        <CardContent className="stack-center gap-3 text-center">
          {amount != null && <CurrencyDisplay amount={amount} currency={currency} className="text-4xl" centsClassName="text-2xl" />}
          {formattedDate && <p className="text-sm font-medium">{t('payment.scheduledPanel.on', { date: formattedDate })}</p>}
          <p className="text-sm text-muted-foreground">{t('payment.scheduledPanel.description')}</p>
        </CardContent>
      </Card>

      <Card variant="fullscreen">
        <CardContent className="stack gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" disabled={isCancelling} className="w-full">
                {t('payment.scheduledPanel.cancel')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('payment.scheduledPanel.cancelDialogTitle')}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t('payment.scheduledPanel.cancelDialogDescription', { date: formattedDate })}
                  {chargeAlreadyInFlight && <> {t('payment.scheduledPanel.cancelDialogInFlightWarning')}</>}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="sm:flex-col-reverse sm:justify-normal">
                <AlertDialogCancel disabled={isCancelling} className="w-full">
                  {t('payment.scheduledPanel.keepScheduled')}
                </AlertDialogCancel>
                <AlertDialogAction
                  // Keep the dialog open on confirm so the in-flight "Cancelling…" state
                  // and disabled controls stay visible until the parent unmounts/navigates
                  // (Radix auto-closes on click otherwise).
                  onClick={(e) => {
                    e.preventDefault();
                    onCancel();
                  }}
                  disabled={isCancelling}
                  className="w-full bg-destructive hover:bg-destructive/90"
                >
                  {isCancelling ? t('payment.scheduledPanel.cancelling') : t('payment.scheduledPanel.confirmCancel')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          {error && <p className="text-sm text-destructive text-center">{error}</p>}
        </CardContent>
      </Card>
    </div>
  );
}
